import React, { useEffect, useState } from "react";
import { Search, Users, Info } from "lucide-react";
import { api } from "../lib/api";
import { formatINR as fmtMoney } from "../lib/format";
import ModuleEmptyState from "../components/app/ModuleEmptyState";

export default function Customers() {
  const [items, setItems] = useState([]);
  const [identity, setIdentity] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/intelligence/customer-identity");
        setIdentity(data);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/customers${search ? `?search=${encodeURIComponent(search)}` : ""}`);
        setItems(data);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  // No customer identity available at all (only anonymous records) — show informative state
  const anonymousOnly = identity && identity.total_customers > 0 && identity.with_identity === 0;
  const noCustomers = (identity && identity.total_customers === 0) || (!loading && items.length === 0 && !search);

  if (noCustomers && !loading) {
    return (
      <div className="max-w-7xl mx-auto" data-testid="customers-page">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">Customers</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>Customer Intelligence</h1>
        <ModuleEmptyState
          icon={Users}
          title="No customers yet"
          description="Import order history to deduplicate buyers across marketplaces and rank them by lifetime value."
          testid="customers-empty"
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="customers-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">Customers</p>
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
        Customer Intelligence
      </h1>
      <p className="mt-2 text-zinc-400 max-w-2xl">Every buyer across every source, deduplicated and ranked by spend.</p>

      {anonymousOnly && (
        <div className="mt-8 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 flex gap-3" data-testid="customers-anonymous-banner">
          <Info className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-200">
              Your marketplace does not expose customer identity
            </p>
            <p className="mt-1 text-sm text-zinc-400">
              The platforms you've imported from ({identity.anonymous} anonymous buyers) don't share emails, phones, or real names.
              Milkath created internal customer records so revenue analytics still work, but identity-based features
              (segmentation, lifecycle campaigns) will activate only when identity data is available.
            </p>
          </div>
        </div>
      )}

      <div className="mt-8 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input
          value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…"
          className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/40 focus:border-orange-600"
          data-testid="customers-search"
        />
      </div>

      <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : noCustomers ? (
          <div className="p-16 text-center" data-testid="customers-empty">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-orange-600/10 border border-orange-600/20 flex items-center justify-center mb-4">
              <Users className="h-6 w-6 text-orange-500" />
            </div>
            <h3 className="text-lg font-medium text-zinc-100">No customers yet</h3>
            <p className="mt-2 text-sm text-zinc-400">Import order history to populate this list.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-zinc-500 border-b border-zinc-800">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Country</th>
                  <th className="px-5 py-3 font-medium text-right">Orders</th>
                  <th className="px-5 py-3 font-medium text-right">Total spent</th>
                  <th className="px-5 py-3 font-medium">Last order</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.customer_id} className="border-b border-zinc-800/60 hover:bg-zinc-900/60" data-testid={`customer-row-${c.customer_id}`}>
                    <td className="px-5 py-3">
                      <span className={c.name_generated ? "text-zinc-400 italic" : "text-zinc-100"}>{c.name}</span>
                      {c.name_generated && <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-600">auto</span>}
                    </td>
                    <td className="px-5 py-3 text-zinc-400 truncate max-w-[220px]">{c.email || "—"}</td>
                    <td className="px-5 py-3 text-zinc-400">{c.country || "—"}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-zinc-200">{c.total_orders}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-orange-400 font-medium">{fmtMoney(c.total_spent)}</td>
                    <td className="px-5 py-3 text-zinc-400 text-xs">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
